<template>
  <div data-test-id="timePicker" v-if="mode === EditorMode.Edit">
    <div v-if="label?.length > 0" class="header">
      {{ label }}<span v-if="required" style="margin-left: 0.1rem" class="required-text">*</span>
    </div>
    <div style="display: inline-flex">
      <PrimarySelect
        :disabled="disabled"
        :error="!isValid"
        :focused="focused"
        :hide-details="true"
        :label="t('labels.hours')"
        :readonly="readonly"
        v-model="model.hours"
        :width="60"
        :items="hourOptions"
        @update:model-value="onTimeChange"
      ></PrimarySelect>
      <span class="separator">:</span>
      <PrimarySelect
        :disabled="disabled"
        :error="!isValid"
        :focused="focused"
        :hide-details="true"
        :label="t('labels.minutes')"
        :readonly="readonly"
        v-model="model.minutes"
        :width="60"
        :items="minuteOptions"
        @update:model-value="onTimeChange"
      ></PrimarySelect>
      <div v-if="twelveHourClock && !durationMode" class="meridiem" @click="onClickMeridiem">
        {{ model.meridiem }}
      </div>
    </div>
    <div class="error-text" v-if="!isValid">{{ errorMessages.join() }}</div>
  </div>
  <DisplayText v-else data-test-id="timePickerDisplay" :value="displayValue" :label="label" />
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { TimeValue } from '@/classes/TimeValue'
import { EditorMode } from '@/enums/EditorMode'
import { computed, onMounted, onUpdated, ref, watch, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ValidationRule } from 'vuetify'
import { useValidation } from 'vuetify/lib/composables/validation.mjs'

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const props = defineProps({
  date: {
    type: Date
  },
  disabled: { type: Boolean },
  durationMode: { type: Boolean, default: false },
  focused: { type: Boolean },
  hourInterval: { type: Number, default: 1 },
  label: { type: String, default: '' },
  maxErrors: { type: [String, Number], default: 1 },
  maxMinutes: { type: Number, default: 59 },
  maxHours: { type: Number, default: 23 },
  minMinutes: { type: Number, default: 0 },
  minHours: { type: Number, default: 0 },
  minuteInterval: { type: Number, default: 5 },
  mode: { type: String as PropType<EditorMode>, default: EditorMode.Edit },
  readonly: { type: Boolean, default: false },
  required: { type: Boolean, default: true },
  rules: { type: Array<ValidationRule>, default: () => [] },
  twelveHourClock: { type: Boolean, default: false }
})

// REACTIVE VARIABLES
const _date = ref<Date | null>(null)
const model = defineModel<TimeValue>({ default: new TimeValue() })
const emit = defineEmits<{
  (e: 'update:modelValue', model: TimeValue): void
}>()

const { isValid, errorMessages, validate } = useValidation(
  {
    ...props,
    modelValue: { modelRef: model, dateRef: _date },
    error: false,
    errorMessages: null,
    'onUpdate:modelValue': undefined,
    validationValue: undefined,
    name: undefined
  },
  'v-input'
)

// COMPUTED PROPERTIES
const displayValue = computed(() => {
  return model.value.toShortTimeString(props.twelveHourClock)
})

const hourOptions = computed(() => {
  const results = []

  if (props.twelveHourClock) {
    const minHours =
      model.value.hours >= 12 && props.minHours < 12 ? props.minHours + 12 : props.minHours
    const maxHours =
      model.value.hours < 12 && props.maxHours > 12 ? props.maxHours - 12 : props.maxHours
    for (let index = minHours; index <= maxHours; index += props.hourInterval) {
      let title = TimeValue.formatHours(index, true)
      results.push({ title, value: index })
    }
  } else {
    for (let index = props.minHours; index <= props.maxHours; index += props.hourInterval) {
      let title = TimeValue.formatHours(index, false)
      results.push({ title, value: index })
    }
  }
  return results
})

const minuteOptions = computed(() => {
  const results = []
  for (let index = props.minMinutes; index < props.maxMinutes; index += props.minuteInterval) {
    const disabled = model.value.hours === 0 && index === 0 && props.durationMode
    results.push({ title: TimeValue.formatValue(index), value: index, props: { disabled } })
  }
  return results
})

// LIFECYCLE HOOKS
onMounted(() => {
  if (props.date) {
    _date.value = props.date
  }
})

onUpdated(() => {
  if (props.date) {
    _date.value = props.date
  }
})

// WATCHERS
watch(
  (): any => props.date,
  (value) => {
    if (value) _date.value = value
    onTimeChange()
  }
)

// FUNCTIONS
const onClickMeridiem = () => {
  if (model.value.hours >= 12) {
    model.value.hours -= 12
  } else if (model.value.hours < 12) {
    model.value.hours += 12
  }
  validate()
  emit('update:modelValue', model.value)
}

const onTimeChange = () => {
  if (model.value.hours === 0 && model.value.minutes === 0 && props.durationMode) {
    model.value.minutes = minuteOptions.value[1].value
  }
  validate()
  emit('update:modelValue', model.value)
}
</script>

<style lang="scss" scoped>
.error-text {
  color: $color-accent-error;
  font-size: 0.75rem;
}
.header {
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}
.meridiem {
  cursor: pointer;
  background-color: $color-background-accent;
  border-radius: 4px;
  padding: 0.1rem 0.2rem 0 0.2rem;
  margin: 0.25rem 0 0 0.1rem;
}

.separator {
  margin: 0.35rem 0.5rem 0 0.5rem;
}
</style>
